import { createAuthenticatedClient } from "../authenticatedClient";
import { API_BASE_URL } from "../config";
import {
  getIntegrationEndpoints,
  type ApiEndpoint,
  type ClientKind,
} from "../config/integrationEndpoints";
import { safeValidate } from "../safeValidate";
import {
  featureResourceTypesResponseSchema,
  integrationCredentialsSchema,
  integrationSchema,
  integrationTypeFeaturesResponseSchema,
  integrationsResponseSchema,
  testConnectionResponseSchema,
  type FeatureResourceTypesResponse,
  type Integration,
  type IntegrationCredentials,
  type IntegrationTypeFeaturesResponse,
  type IntegrationsResponse,
  type TestConnectionResponse,
} from "../schemas/integration.schema";
import {
  correlationRulesResponseSchema,
  type CorrelationRulesResponse,
  type RuleNode,
} from "../schemas/correlationRules.schema";

// Use API_BASE_URL which handles proxy in development
// In dev: uses /api-proxy (which proxies to VITE_API_BASE_URL, e.g., https://loopback:3000)
// In prod: uses the actual API hostname
// When running as MFE: API_BASE_URL already includes /ECMv6/api
const isMfeMode =
  typeof window !== "undefined" &&
  (window.location.hostname === "loopback" ||
    window.location.pathname.startsWith("/ECMv6") ||
    window.location.pathname.startsWith("/intelligence"));

const onboardingBasePath = `${API_BASE_URL}/ispm/v1/spm/onboarding`;

const hydraBasePath = `${API_BASE_URL}/ispm/hydra/v1/integration`;

const idwAdminBasePath = isMfeMode
  ? `${API_BASE_URL}/idw/admin`
  : `${API_BASE_URL}/api/idw/admin`;

const onboardingClient = createAuthenticatedClient(onboardingBasePath);
const hydraClient = createAuthenticatedClient(hydraBasePath);
const idwAdminClient = createAuthenticatedClient(idwAdminBasePath);

/** Map a ClientKind from the endpoint config to its concrete HTTP client. */
function resolveClient(kind: ClientKind) {
  switch (kind) {
    case "onboarding":
      return onboardingClient;
    case "hydra":
      return hydraClient;
    case "idwAdmin":
      return idwAdminClient;
  }
}

/** Map a ClientKind to its absolute base URL (for fetch() calls that need it). */
function resolveBaseUrl(kind: ClientKind): string {
  switch (kind) {
    case "onboarding":
      return onboardingBasePath;
    case "hydra":
      return hydraBasePath;
    case "idwAdmin":
      return idwAdminBasePath;
  }
}

async function callEndpoint<T>(
  endpoint: ApiEndpoint,
  body?: unknown,
): Promise<T> {
  const client = resolveClient(endpoint.client);
  switch (endpoint.method) {
    case "GET":
      return client.get<T>(endpoint.path);
    case "POST":
      return client.post<T>(endpoint.path, body);
    case "PUT":
      return client.put<T>(endpoint.path, body);
    case "DELETE":
      return client.delete<T>(endpoint.path);
  }
}

export interface CreateEndpointWithIntegrationPayload {
  displayName: string;
  integrationType: string;
  metadata?: Record<string, unknown>;
}

export interface GetIntegrationsParams {
  integrationType?: string;
  connectionStatus?: string;
  sortField?: string;
  sortDirection?: string;
  displayName?: string;
  onlyEnabled?: boolean;
  nextPageToken?: string;
  pageSize?: number;
}

export const integrationApi = {
  /**
   * Create endpoint with integration (e.g. GCP Vertex AI).
   * POST {idwAdmin}/integrationProvisioning/createEndpoint
   */
  createEndpointWithIntegration: async (
    payload: CreateEndpointWithIntegrationPayload,
  ): Promise<{ id?: number; [key: string]: unknown }> => {
    const data = await idwAdminClient.post<{
      id?: number;
      [key: string]: unknown;
    }>("/integrationProvisioning/createEndpoint", payload);
    return data;
  },

  getIntegrations: async (
    params?: GetIntegrationsParams,
  ): Promise<IntegrationsResponse> => {
    const config = getIntegrationEndpoints();
    const queryParams = new URLSearchParams();
    if (params?.integrationType) {
      queryParams.append("integrationType", params.integrationType);
    }
    if (params?.connectionStatus) {
      queryParams.append("connectionStatus", params.connectionStatus);
    }
    if (params?.sortField) {
      queryParams.append("sortField", params.sortField);
    }
    if (params?.sortDirection) {
      queryParams.append("sortDirection", params.sortDirection);
    }
    if (params?.displayName) {
      queryParams.append("displayName", params.displayName);
    }
    if (params?.onlyEnabled !== undefined) {
      queryParams.append("onlyEnabled", params.onlyEnabled.toString());
    }
    if (params?.nextPageToken) {
      queryParams.append("nextPageToken", params.nextPageToken);
    }
    if (params?.pageSize !== undefined) {
      queryParams.append("pageSize", params.pageSize.toString());
    }
    const queryString = queryParams.toString();
    const endpoint = config.api.list;
    const path = queryString
      ? `${endpoint.path}?${queryString}`
      : endpoint.path;
    const data = await resolveClient(endpoint.client).get<unknown>(path);
    return safeValidate(
      integrationsResponseSchema,
      data,
      "getIntegrations",
    ) as IntegrationsResponse;
  },

  getIntegrationTypeFeatures: async (
    integrationType: string,
  ): Promise<IntegrationTypeFeaturesResponse> => {
    const endpoint =
      getIntegrationEndpoints().api.getTypeFeatures(integrationType);
    const data = await callEndpoint<IntegrationTypeFeaturesResponse>(endpoint);
    return safeValidate(
      integrationTypeFeaturesResponseSchema,
      data,
      "getIntegrationTypeFeatures",
    );
  },

  getFeatureResourceTypes: async (
    featureName: string,
  ): Promise<FeatureResourceTypesResponse> => {
    const endpoint =
      getIntegrationEndpoints().api.getFeatureResourceTypes(featureName);
    const data = await callEndpoint<FeatureResourceTypesResponse>(endpoint);
    return safeValidate(
      featureResourceTypesResponseSchema,
      data,
      "getFeatureResourceTypes",
    );
  },

  getIntegrationById: async (integrationId: number): Promise<Integration> => {
    const endpoint = getIntegrationEndpoints().api.getById(integrationId);
    const data = await callEndpoint<unknown>(endpoint);
    return safeValidate(
      integrationSchema,
      data,
      "getIntegrationById",
    ) as Integration;
  },

  getIntegrationCredentials: async (
    integrationId: number,
  ): Promise<IntegrationCredentials> => {
    const endpoint =
      getIntegrationEndpoints().api.getCredentials(integrationId);
    const data = await callEndpoint<IntegrationCredentials>(endpoint);
    return safeValidate(
      integrationCredentialsSchema,
      data,
      "getIntegrationCredentials",
    );
  },

  deleteIntegration: async (integrationId: number): Promise<void> => {
    const endpoint = getIntegrationEndpoints().api.delete(integrationId);
    await callEndpoint<void>(endpoint);
  },

  downloadCftFile: async (
    accountId: string,
    featureNames: string[],
    allowedRegions: string[],
    integrationId?: number,
  ): Promise<void> => {
    const endpoint = getIntegrationEndpoints().api.downloadCft;
    const url = `${resolveBaseUrl(endpoint.client)}${endpoint.path}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = localStorage.getItem("accessToken");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: endpoint.method,
      headers,
      body: JSON.stringify({
        accountId,
        featureNames,
        allowedRegions,
        integrationId,
      }),
    });

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error("AWS_ACCOUNT_ID_REQUIRED");
      }
      throw new Error("Failed to download CFT file");
    }

    const contentDisposition = response.headers.get("Content-Disposition");
    const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
    const filename = filenameMatch?.[1] || `${accountId}.yaml`;

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", downloadUrl);
    link.setAttribute("download", filename);
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  /** Test connection with a generic payload (e.g. GCP service account, AWS role, etc.) */
  testConnectionWithPayload: async (payload: {
    integrationType: string;
    credentials: {
      type: string;
      data: Record<string, unknown>;
      metadata?: Record<string, unknown>;
    };
    integrationParameters?: Record<string, unknown>;
  }): Promise<TestConnectionResponse> => {
    const endpoint = getIntegrationEndpoints().api.testConnection;
    const body = {
      ...payload,
      integrationParameters: payload.integrationParameters ?? {},
    };
    const data = await callEndpoint<TestConnectionResponse>(endpoint, body);
    return safeValidate(testConnectionResponseSchema, data, "testConnection");
  },

  testConnection: async (
    integrationType: string,
    roleArn: string,
    externalId: string,
  ): Promise<TestConnectionResponse> => {
    return integrationApi.testConnectionWithPayload({
      integrationType,
      credentials: {
        type: "AWS_ROLE",
        data: { roleArn, externalId },
      },
    });
  },

  createIntegration: async (
    integrationType: string,
    payload: {
      displayName: string;
      isEnabled: boolean;
      credentials: {
        type: string;
        data: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      };
      features: Array<{
        featureName: string;
        featureConfig?: { regions: string[] };
        schedules: Array<{
          scheduleName: string;
          cronExpression: string;
        }>;
      }>;
      metadata: {
        accountId: string;
        description?: string;
        data?: Record<string, unknown>;
      };
    },
  ): Promise<{ id: number }> => {
    const endpoint = getIntegrationEndpoints().api.create(integrationType);
    // When targeting idwAdmin createEndpoint, the backend expects the type
    // embedded in the body (single endpoint for many types). When targeting
    // onboarding/integrations/{type}, the type is already in the URL.
    const body =
      endpoint.client === "idwAdmin"
        ? { ...payload, integrationType }
        : payload;
    return callEndpoint<{ id: number }>(endpoint, body);
  },

  updateIntegration: async (
    integrationId: number,
    payload: {
      integrationId: number;
      displayName: string;
      isEnabled: boolean;
      integrationType?: string;
      credentials: {
        type: string;
        data: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      };
      integrationParameters?: Record<string, unknown>;
      features: Array<{
        featureName: string;
        featureConfig?: { regions: string[] };
        schedules: Array<{
          scheduleName: string;
          cronExpression: string;
        }>;
      }>;
      metadata: {
        accountId: string;
        description?: string;
        data?: Record<string, unknown>;
      };
    },
  ): Promise<{ id: number }> => {
    const endpoint = getIntegrationEndpoints().api.update(integrationId);
    return callEndpoint<{ id: number }>(endpoint, payload);
  },

  getIntegrationTypes: async (
    category?: "application" | "identitySources",
  ): Promise<
    Array<{
      id: number;
      typeName: string;
      displayName: string;
      description: string;
      iconUrl: string;
      isEnabled: boolean;
      isPrivateApp: boolean;
      encryptionConfig: string;
      value: string;
      label: string;
    }>
  > => {
    interface IntegrationTypeResponse {
      integrationTypes: Array<{
        id: number;
        typeName: string;
        displayName: string | null;
        description: string;
        iconUrl: string | null;
        isEnabled: boolean;
        isPrivateApp: boolean;
        encryptionConfig: string;
      }>;
    }
    const baseEndpoint = getIntegrationEndpoints().api.getTypes;
    let path = baseEndpoint.path;
    if (category) {
      const queryParams = new URLSearchParams({ type: category });
      path = `${baseEndpoint.path}?${queryParams.toString()}`;
    }
    const data = await resolveClient(
      baseEndpoint.client,
    ).get<IntegrationTypeResponse>(path);
    return data.integrationTypes
      .filter((item) => item.isEnabled)
      .map((item) => ({
        ...item,
        displayName: item.displayName || item.typeName,
        iconUrl: item.iconUrl || "",
        value: item.typeName.toLowerCase(),
        label: item.displayName || item.typeName,
      }));
  },

  /**
   * Fetch user/account fields available for correlation rules.
   * ISPM-host only — the endpoint lives under the onboarding base path.
   */
  getCorrelationRuleFields: async (
    entityName: string,
  ): Promise<CorrelationRuleFieldsResponse> => {
    return onboardingClient.get<CorrelationRuleFieldsResponse>(
      `/rules/fields/${entityName}`,
    );
  },

  getCorrelationRules: async (
    integrationId: number,
  ): Promise<CorrelationRulesResponse> => {
    const data = await onboardingClient.get<unknown>(
      `/integrations/${integrationId}/rules?rule_type=user-account-correlation`,
    );
    return safeValidate(
      correlationRulesResponseSchema,
      data,
      "getCorrelationRules",
    ) as CorrelationRulesResponse;
  },

  addCorrelationRules: async (
    integrationId: number,
    rules: RuleNode[],
  ): Promise<CorrelationRulesResponse> => {
    const payload = {
      rules: [
        {
          ruleType: "user-account-correlation",
          rule: rules[0],
        },
      ],
    };

    const data = await onboardingClient.post<unknown>(
      `/integrations/${integrationId}/rules`,
      payload,
    );
    return safeValidate(
      correlationRulesResponseSchema,
      data,
      "addCorrelationRules",
    ) as CorrelationRulesResponse;
  },

  replaceCorrelationRules: async (
    integrationId: number,
    rules: RuleNode[],
  ): Promise<CorrelationRulesResponse> => {
    const data = await onboardingClient.put<unknown>(
      `/integrations/${integrationId}/rules/user-account-correlation`,
      { rules },
    );
    return safeValidate(
      correlationRulesResponseSchema,
      data,
      "replaceCorrelationRules",
    ) as CorrelationRulesResponse;
  },

  deleteCorrelationRule: async (
    integrationId: number,
    ruleId: number,
  ): Promise<void> => {
    await onboardingClient.delete<void>(
      `/integrations/${integrationId}/rules/${ruleId}`,
    );
  },
};

export interface CorrelationRuleFieldsResponse {
  fields: string[];
}
